import type { LeadScript } from '../types/leadScript';

export const leadScripts: LeadScript[] = [
  {
    source: 'servicing',
    name: 'Servicing Leads',
    sections: [
      {
        id: 'opening',
        title: 'Opening',
        lines: [
          { kind: 'lo', text: "Hey {firstName}! This is {userFirstName} reaching out from the VA department over here at United Mortgage. How is your day going?" },
          { kind: 'cue', text: 'Wait for response' },
        ],
      },
      {
        id: 'verify',
        title: 'Verify current loan',
        lines: [
          { kind: 'lo', text: "I'm reaching out because we specialize in working with clients like you who are currently with other lenders. At United Mortgage Lending, we work closely with UWM, the #1 lender in the country, to give our clients access to some of the most competitive rates and exclusive programs available. I see here that your current loan is with {lenderName}, is that right?" },
          { kind: 'cue', text: 'Pause for response' },
          { kind: 'lo', text: "And as far as your current rate goes, it's showing here that you're currently at {interestRate}%. Is that right?" },
          { kind: 'cue', text: 'Pause for response' },
          { kind: 'lo', text: "Wow, that's high! You qualify to lower that quite significantly. As far as your current balance goes, it's showing here in my system your balance is sitting at about {mortgageBalance}, is that correct?" },
        ],
      },
      {
        id: 'cashout',
        title: 'Low rate? Pivot to cash-out',
        lines: [
          { kind: 'note', text: "If the client already has a low rate, go into their home's value and their equity for a cash-out refinance." },
          { kind: 'lo', text: "We are currently helping thousands of our veteran homeowners take advantage of their home's equity to consolidate their high interest debts and lower their monthly obligations significantly. How much do you pay towards your credit cards and car loans each month?" },
          { kind: 'lo', text: "Wow, that's high. We're definitely going to be able to get those obligations down for you." },
          { kind: 'cue', text: 'Go into the application, build rapport, and present the deal using the presentation script.' },
        ],
      },
      {
        id: 'notes',
        title: 'Notes',
        lines: [
          { kind: 'note', text: 'If balance, rate, or date of the original mortgage are incorrect, re-verify for accuracy.' },
          { kind: 'note', text: 'Get the current mortgage payment and dig deep into escrows so we can compare P&I on the old mortgage vs. the new mortgage correctly.' },
        ],
      },
    ],
  },
  {
    source: 'prospect',
    name: 'Prospect Lead',
    sections: [
      {
        id: 'opening',
        title: 'Opening',
        lines: [
          { kind: 'lo', text: 'Hey {firstName}! This is {userFirstName} with the {loanType} department at United Mortgage. How are you?' },
          { kind: 'borrower', text: 'Good, how are you?' },
        ],
      },
      {
        id: 'rate',
        title: 'Verify rate',
        lines: [
          { kind: 'lo', text: "I'm doing great, thank you for asking. I am reaching out regarding your current loan at {propertyAddress} as you are currently at {interestRate}% and qualify to lower that rate quite significantly. Can you confirm that the {interestRate}% rate is correct?" },
          { kind: 'borrower', text: 'Yes, that is correct.' },
          { kind: 'lo', text: "Wow, that's high. We're going to help you lower that significantly. {firstName}, I also want to take a step back for a second and thank you for your service. It really means a lot to me and everyone over here at our company. I really respect everything that you do, and welcome home." },
        ],
      },
      {
        id: 'balance',
        title: 'Balance & payment',
        lines: [
          { kind: 'lo', text: 'So with that being said, I see here your current balance is sitting at {firstMortgageBalance}. Is that correct?' },
          { kind: 'borrower', text: 'Yes.' },
          { kind: 'lo', text: 'Perfect. As far as your current mortgage payment goes, how much are you currently paying monthly?' },
          { kind: 'borrower', text: '$1,500' },
          { kind: 'lo', text: "Wow, that's high. Is that including escrows for taxes and insurance?" },
          { kind: 'borrower', text: 'Yes.' },
          { kind: 'cue', text: 'Proceed with taking the rest of the application and present the new deal.' },
        ],
      },
    ],
  },
  {
    source: 'directMail',
    name: 'Direct Mail',
    sections: [
      {
        id: 'greeting',
        title: 'Inbound greeting',
        lines: [
          { kind: 'note', text: 'Inbound mailer will pop up in the Five9 tab when a lead calls in.' },
          { kind: 'lo', text: 'Thank you for calling United Mortgage Lending, this is {userFullName}. Who do I have the pleasure of speaking with?' },
          { kind: 'borrower', text: '{firstName}' },
          { kind: 'lo', text: 'Hi {firstName}, can you please provide me with your notice number? (It can sometimes be at the bottom middle of the letter as well.)' },
          { kind: 'borrower', text: 'VAJ5757574747' },
          { kind: 'cue', text: 'Type the number into the Salesforce search bar and click on the lead contact.' },
          { kind: 'cue', text: 'Click on the insights URL to see all property data.' },
        ],
      },
      {
        id: 'equity',
        title: 'Verify equity',
        lines: [
          { kind: 'lo', text: "Thank you for calling in today, {firstName}. We are helping tons of homeowners like yourself take advantage of their home's equity to consolidate high interest debt and even use it for investing, as rates have dropped significantly in the last 30 days. I see here your current mortgage balance is sitting at about $149,000, does that sound right?" },
          { kind: 'borrower', text: 'Yes.' },
          { kind: 'lo', text: "And as far as your home's current value, it's showing here in my system that your home is worth about $275,000. You know the area better than I do, what would you say your home is worth?" },
          { kind: 'borrower', text: 'That sounds about right.' },
        ],
      },
      {
        id: 'goals',
        title: 'Goals',
        lines: [
          { kind: 'lo', text: "Great, definitely a ton of equity there to take advantage of. What would be your goals with accessing your home's equity? Would it be consolidating high interest debt, remodeling the home, or maybe having some cash on hand for investments?" },
          { kind: 'borrower', text: 'Consolidating my credit card debt.' },
          { kind: 'lo', text: "Great, I am helping all of my clients with that at the moment. I know how tough and stressful it is getting a bunch of bills in the mail at the end of each month. Let's go ahead and help you pay all of that off and lower your monthly obligations quite significantly." },
        ],
      },
      {
        id: 'debt',
        title: 'Debt & cash needed',
        lines: [
          { kind: 'lo', text: 'How much cash would you need in order to pay off all of your debt?' },
          { kind: 'note', text: 'Dig deep into the different liability types such as credit card debt and car loans.' },
          { kind: 'borrower', text: '$50,000' },
          { kind: 'lo', text: "Great, we're definitely going to be able to go ahead and help you out with that. How much are you currently paying each month for all of the credit cards that we are paying off?" },
          { kind: 'borrower', text: '$1,000' },
          { kind: 'lo', text: "Wow, that's extremely high. Did we want any additional cash out to put money aside for a rainy day?" },
          { kind: 'borrower', text: 'No, I just want to pay off my debt.' },
          { kind: 'lo', text: "Great, we're definitely going to be able to get that done." },
          { kind: 'cue', text: 'Proceed with the rest of the application and go into your presentation.' },
        ],
      },
      {
        id: 'notes',
        title: 'Notes & tips',
        lines: [
          { kind: 'branch', label: 'If they ask why they got this letter', text: 'I can absolutely get you some more information regarding that. Can I have your notice number? You can find this at the top right corner of your letter.' },
          { kind: 'note', text: "Once the notice number is received, let them know they're receiving this letter because they have a substantial amount of equity to take advantage of. Immediately verify the data on the insights URL: current mortgage balance, appraised value, and current payment. This puts you right at the home value question." },
          { kind: 'note', text: 'Quick tip: when the client asks what type of program this is, check the insights portal for their last refinance and refer back to it ("this is exactly what you did back on September 19th, 2019 when you did a cash-out refinance"). This builds trust.' },
          { kind: 'note', text: 'Never leave a response open ended. Make a statement, then follow through with a question about their current situation, such as verifying mortgage balance or appraised value.' },
          { kind: 'cue', text: 'Always control the call.' },
        ],
      },
    ],
  },
  {
    source: 'vaCashout',
    name: 'VA Cashout Prospect',
    sections: [
      {
        id: 'intro',
        title: 'Intro',
        lines: [
          { kind: 'lo', text: 'Hey {firstName}! This is {userFirstName} reaching out from the VA department over here at United Mortgage. How are you?' },
          { kind: 'borrower', text: 'Good, how are you?' },
        ],
      },
      {
        id: 'debt',
        title: 'Monthly debt',
        lines: [
          { kind: 'lo', text: "Good, thank you for asking. We are reaching out as we are currently helping thousands of our veteran homeowners take advantage of their home's equity to consolidate high interest debt and lower their monthly obligations significantly. So with that being said, {firstName}, how much do you currently pay each month towards your debt, excluding your mortgage?" },
          { kind: 'borrower', text: '$1,000/month' },
          { kind: 'lo', text: "Wow, that's high, we will definitely be able to help you lower that quite significantly. If you can give me a rough estimate, how much do you owe currently on those debts?" },
          { kind: 'borrower', text: '$50,000' },
        ],
      },
      {
        id: 'property',
        title: 'Verify property',
        lines: [
          { kind: 'lo', text: 'Also, I see here your current balance is sitting at about {mortgageBalance}, does that sound correct?' },
          { kind: 'borrower', text: 'Yes.' },
          { kind: 'lo', text: "Perfect. It's showing here that your home is worth about (Zillow valuation), does that sound correct?" },
          { kind: 'borrower', text: 'Yes.' },
          { kind: 'lo', text: 'Perfect, how much do you pay each month towards your current mortgage payment?' },
          { kind: 'borrower', text: '$1,500/month' },
          { kind: 'note', text: 'Dig deep and find out how much of this goes towards escrow so we know what they are truly saving as far as P&I goes.' },
          { kind: 'cue', text: 'Proceed with taking the rest of the application, presenting the new deal, and recapping the benefits of getting this done.' },
        ],
      },
      {
        id: 'notes',
        title: 'Notes',
        lines: [
          { kind: 'note', text: 'Dig deeper into the goals. Ask if they have any use for additional cash for investments, home renovations, or cash set aside for a rainy day.' },
          { kind: 'note', text: "Be assumptive and assume it's a done deal. Don't ask \"do you want to proceed?\" Instead, ask if they have any questions about the deal, then go straight into credit and setting expectations: documents needed, and that you'll reach back out to go over disclosures within 30–40 minutes. Have the client save your direct and office numbers." },
          { kind: 'note', text: 'Use the Salesforce fields that estimate their current balances on car loans and credit card debt as leverage if the borrower is not giving much information.' },
        ],
      },
    ],
  },
  {
    source: 'internet',
    name: 'Internet',
    sections: [
      {
        id: 'opening',
        title: 'Opening',
        lines: [
          { kind: 'lo', text: "Hey {firstName}! This is {userFirstName} with United. I'm reaching out regarding your inquiry with our company to lower your monthly payments at {propertyAddress}. {firstName}, were you looking to pull cash out or just lower your rate and payment?" },
        ],
      },
      {
        id: 'not-interested',
        title: 'Not interested',
        lines: [
          { kind: 'branch', label: 'Not interested / did not inquire', text: "Got it. I completely respect the fact that you're not interested, although I do see here that you did inquire within the last couple of weeks to lower your monthly payments. What has made you change your mind from then until now?" },
          { kind: 'branch', label: 'Still no interest', text: "Not a problem at all, let me go ahead and update your file so that you can stop getting all of these calls. Now while I have you on the phone, all my clients do a mortgage review at least annually to ensure they're in the best spot possible regarding their mortgage. I see here your current interest rate is sitting at about {interestRate}%, is that correct?" },
        ],
      },
      {
        id: 'can-lower',
        title: 'If we can lower the rate',
        lines: [
          { kind: 'lo', text: "Wow, that's high, we're definitely going to be able to help lower that rate by quite a bit. Now I see here your current balance is sitting at about {mortgageBalance}, is that correct?" },
          { kind: 'note', text: 'If any of the data points are inaccurate, ask the borrower to confirm their rate and let them know we just updated their file.' },
        ],
      },
      {
        id: 'cant-lower',
        title: "If we can't lower the rate",
        lines: [
          { kind: 'note', text: 'Leverage the Salesforce soft credit report so we can pull all current liabilities and create a sale.' },
          { kind: 'lo', text: "What all of my clients are doing right now is leveraging their home's equity to consolidate their high interest debt to lower their monthly obligations significantly. Now how much are you currently paying each month towards your monthly debt?" },
          { kind: 'note', text: "If we have the credit report, skip the question — just verify the debt and say \"Wow, that's high! We're definitely going to help you lower those payments by quite a bit.\" Then verify their current mortgage characteristics." },
          { kind: 'note', text: "To run a soft pull in Salesforce, use the borrower's SSN and DOB. If not received, enter 000-00-9999 with your own date of birth to pull the borrower's liabilities. Do this during the mortgage review. Be creative and create the sale." },
        ],
      },
    ],
  },
  {
    source: 'cashoutProspect',
    name: 'Cashout Prospect',
    sections: [
      {
        id: 'opening',
        title: 'Opening',
        lines: [
          { kind: 'lo', text: 'Hey {firstName}! This is {userFirstName} with United Mortgage — how are you today?' },
          { kind: 'borrower', text: 'Good, how are you?' },
          { kind: 'lo', text: "I'm doing great, thanks for asking. I'll be quick — I'm actually reaching out regarding your property on {propertyAddress}. Based on recent data, it looks like you've built up a good amount of equity in your home." },
          { kind: 'cue', text: 'Short pause' },
          { kind: 'lo', text: 'Have you looked at consolidating any high interest debt or any possible renovations?' },
        ],
      },
      {
        id: 'response',
        title: 'Based on their answer',
        lines: [
          { kind: 'branch', label: 'Yes', text: 'I hear you. I see you have around {creditCardDebt} in high interest debt. Is that from credit cards or personal loans?' },
          { kind: 'branch', label: 'No', text: 'Got it. The reason I ask is, based on your current loan at around {interestRate}%, there may be an opportunity to not only access some of that equity, but potentially improve your overall financial situation monthly.' },
        ],
      },
      {
        id: 'verify',
        title: 'Verify loan details',
        lines: [
          { kind: 'lo', text: "Just so I have this right — you're currently around {interestRate}%, correct?" },
          { kind: 'lo', text: "Perfect. It's showing here that your home is worth about (Zillow valuation), does that sound correct?" },
          { kind: 'borrower', text: 'Yes.' },
          { kind: 'lo', text: 'Perfect, how much do you pay each month towards your current mortgage payment?' },
          { kind: 'borrower', text: '$1,500/month' },
          { kind: 'note', text: 'Dig deep and find out how much of this goes towards escrow so we know what they are truly saving as far as P&I goes.' },
          { kind: 'cue', text: 'Proceed with taking the rest of the application, presenting the new deal, and recapping the benefits of getting this done.' },
        ],
      },
    ],
  },
  {
    source: 'rateTermIrrrl',
    name: 'Rate & Terms / IRRRL',
    sections: [
      {
        id: 'opening',
        title: 'Opening',
        lines: [
          { kind: 'lo', text: 'Hey {firstName}! This is {userFirstName} with the {loanType} department at United Mortgage. How are you?' },
          { kind: 'borrower', text: 'Good, how are you?' },
          { kind: 'lo', text: "I'm doing great, thank you for asking. I am reaching out regarding your current loan at {propertyAddress} as you are currently at {interestRate}% and qualify to lower that rate quite significantly. Can you confirm that the {interestRate}% rate is correct?" },
          { kind: 'branch', label: '"I\'m not interested"', text: "I definitely understand that. Now I'm not asking you to make any changes regarding your mortgage, all I'm asking for is just a couple of minutes of your time to ensure you're in the best position possible when it comes to your mortgage. Now, I see here that your current interest rate is sitting at about {interestRate}%, does that sound right?" },
        ],
      },
      {
        id: 'can-lower',
        title: 'If we can lower the rate',
        lines: [
          { kind: 'lo', text: "Wow, that's high, we're definitely going to be able to help lower that rate by quite a bit. Now I see here your current balance is sitting at about {mortgageBalance}, is that correct?" },
          { kind: 'note', text: 'If any of the data points are inaccurate, ask the borrower to confirm their rate and let them know we just updated their file.' },
        ],
      },
      {
        id: 'cant-lower',
        title: "If we can't lower the rate",
        lines: [
          { kind: 'note', text: 'Leverage the Salesforce soft credit report so we can pull all current liabilities and create a sale.' },
          { kind: 'lo', text: "What all of my clients are doing right now is leveraging their home's equity to consolidate their high interest debt to lower their monthly obligations significantly. Now how much are you currently paying each month towards your monthly debt?" },
          { kind: 'note', text: "If we have the credit report, skip the question — just verify the debt and say \"Wow, that's high! We're definitely going to help you lower those payments by quite a bit.\" Then verify their current mortgage characteristics." },
          { kind: 'note', text: "To run a soft pull in Salesforce, use the borrower's SSN and DOB to pull their liabilities. Do this during the mortgage review so we can see liabilities and help lower their monthly obligations. Be creative." },
          { kind: 'cue', text: 'Get the borrower to at least move forward with formal disclosures, so they can see the numbers in black and white and how good this deal truly is.' },
        ],
      },
    ],
  },
  {
    source: 'newHomeListing',
    name: 'New Home Listing',
    sections: [
      {
        id: 'opening',
        title: 'Opening',
        lines: [
          { kind: 'lo', text: 'Hey {firstName}! This is {userFirstName} with United Mortgage Lending — I noticed your home at {propertyAddress} just went on the market. Are you still accepting offers on it right now?' },
        ],
      },
      {
        id: 'active',
        title: 'Still active',
        lines: [
          { kind: 'branch', label: 'If YES (still active)', text: 'Great — and once this one sells, is the plan to buy your next home, or is this more of a downsizing / relocation move?' },
          { kind: 'branch', label: 'If buying next home', text: 'Got it. Does your next purchase depend on this home selling first — meaning you need the proceeds to buy the next one — or do you have funds available either way?' },
          { kind: 'branch', label: 'If YES — contingent', text: "That's really common, and it's exactly what we help coordinate. Do you have five minutes for me to explain what being contingent means for your next offer, and how we keep you from getting stuck in the gap between the two?" },
          { kind: 'note', text: 'If yes, explain: sellers and agents often see contingent offers as weaker or riskier. Getting pre-approved now — before this home even sells — puts them in a stronger, more competitive position.' },
          { kind: 'lo', text: "Want me to get that pre-approval started today, no cost, so you're ready the second the right home comes up?" },
          { kind: 'branch', label: 'If NO — not contingent', text: 'That gives you a lot more flexibility on timing. Do you already have financing lined up for the next purchase, or should we get you pre-approved now so you know exactly what you can offer when you find it?' },
          { kind: 'branch', label: 'If NOT buying (downsizing to rental, moving in with family, etc.)', text: "Understood, appreciate you sharing that. If anything changes, or you know anyone else navigating a similar move, I'd love to stay a resource." },
        ],
      },
      {
        id: 'inactive',
        title: 'No longer accepting offers',
        lines: [
          { kind: 'lo', text: 'Got it — did it already go under contract, or is it off the market for another reason?' },
          { kind: 'branch', label: 'If under contract', text: "Nice. When's the closing date? Have you started looking at your next home, or lined up financing? Let's get you a pre-approval ready so you can move fast once you find it." },
          { kind: 'note', text: 'If off market for other reasons: dig in on what changed, and set a timeline for when to follow back up.' },
        ],
      },
      {
        id: 'objections',
        title: 'Objection handling',
        lines: [
          { kind: 'branch', label: '"I already have a lender / already pre-approved"', text: "When does that pre-approval expire? Most people don't realize they're only good for 60–90 days, and terms shift. Worth 10 minutes to make sure you've still got the best deal before you're on a tight closing timeline?" },
          { kind: 'branch', label: '"Haven\'t thought that far ahead yet"', text: "That's exactly what I'm here for. Let's grab 10 minutes this week so you're not scrambling once your home sells." },
        ],
      },
      {
        id: 'close',
        title: 'Close',
        lines: [
          { kind: 'note', text: 'Every call ends with a specific next step — start the pre-approval now, or lock a callback date and time.' },
          { kind: 'cue', text: 'Log the outcome: contingent / not contingent / not buying / follow-up date.' },
        ],
      },
    ],
  },
  {
    source: 'nerdWallet',
    name: 'NerdWallet Real-Time Purchase',
    sections: [
      {
        id: 'opening',
        title: 'Opening',
        lines: [
          { kind: 'lo', text: "Hey {firstName}! This is {userFirstName} with United Mortgage Lending. Your home purchase request through NerdWallet just came through to me. It looks like you're considering purchasing around {loanAmount} in {propertyCity}, {propertyState}. How soon are you looking to purchase?" },
          { kind: 'lo', text: 'Great. I just want to confirm a few details so I can give you accurate options instead of a generic online quote.' },
        ],
      },
      {
        id: 'discovery',
        title: 'Discovery',
        lines: [
          { kind: 'lo', text: 'Are you currently under contract, actively looking, or just starting the process?' },
          { kind: 'lo', text: 'Are you working with a real estate agent?' },
          { kind: 'lo', text: 'Will this be your primary residence?' },
          { kind: 'lo', text: 'Is this your first home purchase?' },
          { kind: 'lo', text: 'Do you currently own a home that you need to sell?' },
          { kind: 'lo', text: 'What are you thinking about putting down?' },
          { kind: 'lo', text: 'Is the credit range you entered based on something you checked recently?' },
          { kind: 'lo', text: 'What do you do for work, and how long have you been there?' },
          { kind: 'lo', text: 'Do you have a monthly payment you would like to stay around?' },
          { kind: 'lo', text: 'When comparing mortgage options, what matters most to you: the monthly payment, cash needed at closing, or the lowest overall cost?' },
        ],
      },
    ],
  },
];
